// ============================================================================
// DOCUMENT SERIES SERVICE (PMS Phase 2 slice 4)
// ============================================================================
// crs.Api.Controllers.DocumentModule.DocumentSeriesController -> /api/v1/document-series
// crs.Api.Controllers.DocumentModule.RunningSeriesController  -> /api/v1/running-series (GRC-1)

import { get, post, put } from '../client/axiosClient';
import { buildEndpointWithParams, buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse, ID } from '../types';
import type {
  DocumentSeries,
  DocumentSeriesGapReport,
  IssuePendingResult,
  PendingNumbersSummary,
  RunningSeries,
  UpdateDocumentSeriesRequest,
  UpdateRunningSeriesRequest,
} from '../types/documentSeries.types';

const BASE = '/document-series';
const RUNNING = '/running-series';

const unwrap = <T,>(res: { data?: ApiResponse<T> }, fallback: string): T => {
  if (res.data?.data === undefined || res.data?.data === null) throw new Error(res.data?.message || fallback);
  return res.data.data;
};

class DocumentSeriesService {
  async list(propertyId?: ID): Promise<DocumentSeries[]> {
    const res = await get<ApiResponse<DocumentSeries[]>>(buildEndpointWithQuery(BASE, propertyId ? { propertyId } : {}));
    return res.data?.data ?? [];
  }

  /** Creates the property's current-year series; returns why it cannot ('' when ready). */
  async prepare(propertyId: ID): Promise<string> {
    const res = await post<ApiResponse<string>>(buildEndpointWithQuery(`${BASE}/prepare`, { propertyId }), {});
    return res.data?.data ?? '';
  }

  async update(id: ID, payload: UpdateDocumentSeriesRequest): Promise<DocumentSeries> {
    const res = await put<ApiResponse<DocumentSeries>>(buildEndpointWithParams(`${BASE}/:id`, { id }), payload);
    return unwrap(res, 'Update failed.');
  }

  async gaps(id: ID): Promise<DocumentSeriesGapReport> {
    const res = await get<ApiResponse<DocumentSeriesGapReport>>(buildEndpointWithParams(`${BASE}/:id/gaps`, { id }));
    return unwrap(res, 'Gap check failed.');
  }

  async pending(propertyId: ID): Promise<PendingNumbersSummary> {
    const res = await get<ApiResponse<PendingNumbersSummary>>(buildEndpointWithQuery(`${BASE}/pending`, { propertyId }));
    return unwrap(res, 'Could not read pending numbers.');
  }

  async issuePending(propertyId: ID): Promise<IssuePendingResult> {
    const res = await post<ApiResponse<IssuePendingResult>>(buildEndpointWithQuery(`${BASE}/issue-pending`, { propertyId }), {});
    return unwrap(res, 'Issuing pending numbers failed.');
  }

  // ---- running series (GRC-1) -> /api/v1/running-series ---------------------------------

  async runningList(propertyId?: ID): Promise<RunningSeries[]> {
    const res = await get<ApiResponse<RunningSeries[]>>(buildEndpointWithQuery(RUNNING, propertyId ? { propertyId } : {}));
    return res.data?.data ?? [];
  }

  /** Creates the property's running series that do not exist yet (admins). */
  async runningPrepare(propertyId: ID): Promise<void> {
    await post<ApiResponse<unknown>>(buildEndpointWithQuery(`${RUNNING}/prepare`, { propertyId }), {});
  }

  async runningUpdate(id: ID, payload: UpdateRunningSeriesRequest): Promise<RunningSeries> {
    const res = await put<ApiResponse<RunningSeries>>(buildEndpointWithParams(`${RUNNING}/:id`, { id }), payload);
    return unwrap(res, 'Update failed.');
  }

  /** The property's business date (YYYY-MM-DD): local time minus its day cutoff. */
  async businessDate(propertyId: ID): Promise<string> {
    const res = await get<ApiResponse<string>>(buildEndpointWithQuery(`${BASE}/business-date`, { propertyId }));
    return unwrap(res, 'Could not read the business date.');
  }
}

export const documentSeriesService = new DocumentSeriesService();
export default documentSeriesService;
